/**
 * tutorLiveChromeViewModel — les règles pures de la barre, de « Plus », de l'aide et de la fin du live
 * du masteur (`useTutorLiveChrome`).
 *
 * Chaque règle porte le symbole legacy qu'elle reprend, l'ancien pied du tuteur
 * (`common/header/LiveModeFooter.js`) et ce qu'il monte (`ControlBarM`, `TroubleshootingPanel`,
 * `useTroubleshootingDetection`). Pur : aucun crochet, aucune requête.
 */
import { type TutorLiveMoreAction } from "../../../../../design-system/templates/TutorLiveOverlays/TutorLiveOverlays"
import { type UILiveDiagnostic } from "../../../../../design-system/templates/LiveHelpSheet/LiveHelpSheet"
import { type UILiveProblem } from "../../../ui/types/studentLive"
import { type UITutorLiveRoomKind } from "../../../ui/types/tutorLive"
import { principalTutorId, type LiveCastSession } from "../../../../../engine/live/student/liveTutorCast"

/* ──────────────────────────── le compte des non-lus ──────────────────────────── */

type UnreadSession = {
    unread_private_messages_count?: Record<string, number | null | undefined> | unknown[] | null
    unread_messages_count?: number | null
}

/**
 * Le compte du bouton chat : la somme des non-lus de chaque fil privé, plus ceux du groupe.
 *
 * **Corrige l'ancien pied** (`LiveModeFooter.js:552-556`) : il additionnait l'OBJET
 * `unread_private_messages_count` (`{id d'élève: n}`) et un nombre ; le résultat était une chaîne,
 * `"[object Object]n" > 0` était faux, et le point ne s'affichait jamais dès qu'un fil privé existait.
 * La collection vide sort en `[]` (PHP) : elle compte zéro.
 */
export function tutorChatUnreadCount(session: UnreadSession | null | undefined): number {
    if (session == null) return 0
    const privateCounts = session.unread_private_messages_count
    const privateTotal =
        privateCounts == null || Array.isArray(privateCounts)
            ? 0
            : Object.values(privateCounts).reduce<number>((sum, value) => sum + positive(value), 0)
    return privateTotal + positive(session.unread_messages_count)
}

const positive = (value: unknown): number => (typeof value === "number" && value > 0 ? value : 0)

/* ──────────────────────────── « Plus » ──────────────────────────── */

/** Les lignes de « Plus » que l'écran sait traiter (`onAction`). */
export type TutorMoreActionId =
    | "all_dialogue"
    | "lower_hands"
    | "common_activity"
    | "refresh"
    | "resources"
    | "help"
    | "share_screen"
    | "flip_camera"

export const TUTOR_MORE_ACTIONS: Record<TutorMoreActionId, TutorLiveMoreAction> = {
    all_dialogue: { id: "all_dialogue", label: "Tous en Dialogue", icon: "volume-up" },
    lower_hands: { id: "lower_hands", label: "Baisser toutes les mains", icon: "hand-paper" },
    common_activity: { id: "common_activity", label: "Nouvelle activité commune", icon: "plus" },
    refresh: { id: "refresh", label: "Resynchroniser", icon: "sync-alt" },
    resources: { id: "resources", label: "Ressources du tuteur", icon: "book-open" },
    help: { id: "help", label: "Un problème ?", icon: "question-circle" },
    share_screen: { id: "share_screen", label: "Partager l'écran", icon: "desktop" },
    flip_camera: { id: "flip_camera", label: "Retourner la caméra", icon: "camera-rotate" },
}

/** « Resynchroniser » pendant que ses lectures volent. */
export const TUTOR_REFRESHING_LABEL = "Resynchronisation…"

/**
 * Les lignes de « Plus », dans l'ordre de l'ancien ⋮ (`LiveModeFooter.js:578-708`). « Tous en
 * Concentré » n'y est pas : la page la pose en tête (`more.onAllFocused`), et la retire en salle
 * `individual`. « Messages » non plus : le bouton chat de la barre reste à toutes les largeurs.
 *  - « Tous en Dialogue » : pas en salle `individual` ;
 *  - « Partager l'écran » / « Arrêter le partage » : quand la page retire le bouton de la barre (le
 *    téléphone) et que le partage est offert (`masked: !canScreenShare`) ;
 *  - « Retourner la caméra » : quand une autre caméra est là (le natif, l'ancien `useSwitchCamera` ;
 *    le web quand le navigateur nomme une caméra avant et une arrière, un téléphone ou une tablette ; un
 *    ordinateur change de caméra par le « ^ ») ;
 *  - « Resynchroniser » : l'« Actualiser » de l'ancienne liste des élèves (`NewStudentsListItem.js:480-484`,
 *    `:622-647`), quand l'écran le branche (`refresh`). Il tournait tant que ses lectures volaient : « Plus »
 *    n'a pas de roue par ligne, la ligne le dit (« Resynchronisation… ») et un toucher n'en relance pas.
 */
export function tutorMoreActions(args: {
    roomKind: UITutorLiveRoomKind
    /** La page retire le partage de la barre (sous 600 px) : il passe dans « Plus ». */
    shareInMore: boolean
    canShare: boolean
    sharing: boolean
    canFlip: boolean
    /** « Resynchroniser » branché ; `refreshing` : ses lectures volent. Absent : pas de ligne. */
    refresh?: { refreshing: boolean } | null
}): TutorLiveMoreAction[] {
    const { roomKind, shareInMore, canShare, sharing, canFlip, refresh } = args
    const share = {
        ...TUTOR_MORE_ACTIONS.share_screen,
        ...(sharing ? { label: "Arrêter le partage", icon: "desktop-slash" as const } : {}),
    }
    const relire = { ...TUTOR_MORE_ACTIONS.refresh, ...(refresh?.refreshing ? { label: TUTOR_REFRESHING_LABEL } : {}) }
    return [
        ...(roomKind !== "individual" ? [TUTOR_MORE_ACTIONS.all_dialogue] : []),
        TUTOR_MORE_ACTIONS.lower_hands,
        TUTOR_MORE_ACTIONS.common_activity,
        ...(refresh != null ? [relire] : []),
        TUTOR_MORE_ACTIONS.resources,
        TUTOR_MORE_ACTIONS.help,
        ...(shareInMore && canShare ? [share] : []),
        ...(canFlip ? [TUTOR_MORE_ACTIONS.flip_camera] : []),
    ]
}

/* ──────────────────────────── « Un problème ? » ──────────────────────────── */

/**
 * Le problème que l'écran voit déjà (`useTroubleshootingDetection.detectedProblem`) : la connexion
 * faible, sinon le micro (refusé, ou muet au niveau du système panneau ouvert), sinon la caméra
 * refusée. « Plus » porte son point, et l'aide s'ouvre dessus (B-40).
 */
export function tutorDetectedProblem(args: {
    poorConnection: boolean
    micAllowed: boolean | null
    micSilent: boolean
    cameraAllowed: boolean | null
}): UILiveProblem | null {
    if (args.poorConnection) return "connection"
    if (args.micAllowed === false || args.micSilent) return "mic"
    if (args.cameraAllowed === false) return "camera"
    return null
}

/**
 * Toucher le micro ou la caméra en défaut, en natif (`ControlBarM.tsx:105`, `:175`) : l'aide s'ouvre
 * sur ce problème au lieu de basculer l'appareil. Sur le web, le toucher bascule (le « ! » et « Plus »
 * disent le défaut, comme l'ancienne barre web).
 */
export function tutorDeviceTap(args: {
    device: "mic" | "camera"
    blocked: boolean
    native: boolean
}): { kind: "toggle" } | { kind: "help"; problem: UILiveProblem } {
    if (args.native && args.blocked) return { kind: "help", problem: args.device === "mic" ? "mic" : "camera" }
    return { kind: "toggle" }
}

export type TutorHelpContext = {
    web: boolean
    micOn: boolean
    cameraOn: boolean
    micAllowed: boolean | null
    cameraAllowed: boolean | null
    micSilent: boolean
    poorConnection: boolean
    /** La qualité de ma connexion, telle que LiveKit la dit (`ConnectionQuality`). */
    quality: "excellent" | "good" | "poor" | "lost" | "unknown"
}

type HelpStep = UILiveDiagnostic["steps"][number]

const QUALITY_WORDS: Record<TutorHelpContext["quality"], string> = {
    excellent: "Excellente",
    good: "Bonne",
    poor: "Faible",
    lost: "Perdue",
    unknown: "Inconnue",
}

/**
 * Ce que le panneau du tuteur vérifie et propose (`TroubleshootingPanel role="tutor"`,
 * `:428-1065`) : ses verdicts et ses étapes, aux mots du tuteur. Le tuteur est toujours « en
 * Dialogue » (l'ancien pied passe `isSoundMode` à vrai) : « Vérifiez que les élèves sont en mode
 * Dialogue » n'est jamais dit, pas plus que « Lever la main ».
 */
export function tutorHelpDiagnostic(problem: UILiveProblem | null, context: TutorHelpContext): UILiveDiagnostic {
    const { web } = context
    const restart: HelpStep = web
        ? { iconName: "redo", text: "Rafraîchis la page", detail: "Parfois un simple rechargement résout le problème" }
        : {
              iconName: "redo",
              text: "Ferme et rouvre l'application",
              detail: "Parfois un simple redémarrage résout le problème",
          }
    switch (problem) {
        case "mic":
            return {
                notice:
                    context.micAllowed === false
                        ? web
                            ? "Le micro n'est pas autorisé dans ton navigateur. Clique sur le bouton à gauche de la barre d'adresse, autorise le micro, puis rafraîchis la page."
                            : "Le micro n'est pas autorisé. Ouvre les paramètres de l'application pour l'activer."
                        : !context.micOn
                          ? "Ton micro est coupé dans l'application. Clique sur le bouton micro en bas de l'écran pour le réactiver."
                          : context.micSilent
                            ? "Ton micro semble muet au niveau du système. Vérifie le bouton mute de ton clavier ou les paramètres son de ton appareil."
                            : "Ton micro semble fonctionner correctement. Si le problème persiste, suis les étapes ci-dessous.",
                steps: web
                    ? [
                          {
                              iconName: "keyboard",
                              text: "Certains claviers ont une touche qui coupe le micro",
                              detail: "Vérifie le bouton mute sur ton clavier (souvent en haut à droite)",
                          },
                          restart,
                      ]
                    : [
                          {
                              iconName: "redo",
                              text: "Ferme et rouvre l'application",
                              detail: "Cela permet de relancer le micro",
                          },
                          {
                              iconName: "headphones",
                              text: "Essaie de brancher ou débrancher des écouteurs",
                              detail: "Cela peut changer le micro utilisé",
                          },
                          {
                              iconName: "window-close",
                              text: "Ferme les autres applis qui utilisent le micro",
                              detail: "Snap, Teams, Zoom, FaceTime...",
                          },
                      ],
            }
        case "cant_hear":
            return {
                steps: [
                    { iconName: "volume-up", text: "Monte le volume de ton appareil" },
                    web
                        ? {
                              iconName: "headphones",
                              text: "Vérifie le bon haut-parleur",
                              detail: "Si tu as un casque, vérifie qu'il est bien branché",
                          }
                        : {
                              iconName: "headphones",
                              text: "Essaie de brancher ou débrancher des écouteurs",
                              detail: "Cela peut changer le haut-parleur utilisé",
                          },
                    ...(web
                        ? [
                              {
                                  iconName: "microphone",
                                  text: "Change de haut-parleur ou de micro via la flèche sur le bouton micro",
                                  detail: "Il se peut que le mauvais appareil soit sélectionné",
                              } satisfies HelpStep,
                          ]
                        : []),
                    restart,
                ],
            }
        case "camera":
            return {
                notice:
                    context.cameraAllowed === false
                        ? web
                            ? "La caméra n'est pas autorisée dans ton navigateur. Clique sur le bouton à gauche de la barre d'adresse, autorise la caméra, puis rafraîchis la page."
                            : "La caméra n'est pas autorisée. Ouvre les paramètres de l'application pour l'activer."
                        : !context.cameraOn
                          ? "Ta caméra est désactivée. Clique sur le bouton caméra en bas de l'écran pour la réactiver."
                          : "Ta caméra semble fonctionner. Si le problème persiste, suis les étapes ci-dessous.",
                steps: [
                    {
                        iconName: "video",
                        text: "Vérifie que ta caméra n'est pas couverte",
                        detail: "Certains ordinateurs ont un cache physique",
                    },
                    {
                        iconName: "window-close",
                        text: "Ferme les applis qui utilisent la caméra",
                        detail: "Snap, Teams, Zoom, FaceTime...",
                    },
                    ...(web
                        ? []
                        : [
                              {
                                  iconName: "redo",
                                  text: "Ferme et rouvre l'application",
                                  detail: "Cela permet de relancer la caméra",
                              } satisfies HelpStep,
                          ]),
                ],
            }
        case "connection":
            return {
                notice: `Qualité de connexion : ${QUALITY_WORDS[context.quality]}. ${
                    context.poorConnection
                        ? "Ta connexion est instable. Suis les recommandations ci-dessous pour l'améliorer."
                        : "Ta connexion semble bonne. Si le problème persiste, suis les recommandations ci-dessous."
                }`,
                steps: [
                    {
                        iconName: "wifi",
                        text: "Rapproche-toi de ta box Wi-Fi",
                        detail: "Plus tu es proche, meilleur est le signal",
                    },
                    {
                        iconName: "times-circle",
                        text: "Ferme les autres applis et onglets",
                        detail: "Jeux, YouTube, réseaux sociaux...",
                    },
                    web
                        ? {
                              iconName: "wifi",
                              text: "Branche-toi en câble si possible",
                              detail: "Le câble Ethernet est plus stable que le Wi-Fi",
                          }
                        : {
                              iconName: "wifi",
                              text: "Passe en données mobiles (4G/5G)",
                              detail: "Si le Wi-Fi est mauvais, les données mobiles peuvent être plus stables",
                          },
                    ...(web
                        ? []
                        : [
                              {
                                  iconName: "redo",
                                  text: "Ferme et rouvre l'application",
                                  detail: "Cela permet de relancer la connexion",
                              } satisfies HelpStep,
                          ]),
                ],
            }
        default:
            // « Autre problème » : écran noir, activité qui ne charge pas, partage d'écran (`:1005-1065`).
            return {
                steps: [
                    web
                        ? {
                              iconName: "redo",
                              text: "Écran noir ou page blanche : rafraîchis la page",
                              detail: "Ctrl+R ou Cmd+R",
                          }
                        : { iconName: "redo", text: "Écran noir : ferme et rouvre l'application" },
                    { iconName: "wifi", text: "Vérifie ta connexion Internet" },
                    {
                        iconName: "times-circle",
                        text: "Une activité ne charge pas : attends quelques secondes",
                        detail: web
                            ? "Rafraîchis la page si rien ne se passe"
                            : "Ferme et rouvre l'application si rien ne se passe",
                    },
                    {
                        iconName: "video",
                        text: "Les élèves ne voient pas ton écran : vérifie que le partage d'écran est activé",
                    },
                ],
            }
    }
}

/** Les gestes propres à un diagnostic, que le crochet de l'aide branche (`UILiveDiagnostic.actions`). */
export type TutorHelpActionId = "allow_mic" | "allow_camera" | "refresh_after_denial" | "change_mic" | "change_camera"

export type TutorHelpAction = { id: TutorHelpActionId; iconName: HelpStep["iconName"]; label: string; detail?: string }

/** La phrase de l'ancien `PermissionAction` quand la demande relancée est encore refusée. */
export const PERMISSION_STILL_DENIED =
    "Permission non détectée. Clique sur le bouton à gauche de la barre d'adresse pour autoriser."

// L'ancien bouton portait `shield-alt`, absent du registre d'icônes : la coche dit « c'est fait ».
const allowAction = (device: "mic" | "camera", stillDenied: boolean): TutorHelpAction =>
    stillDenied
        ? { id: "refresh_after_denial", iconName: "redo", label: "Rafraîchir la page", detail: PERMISSION_STILL_DENIED }
        : device === "mic"
          ? { id: "allow_mic", iconName: "check-circle", label: "J'ai autorisé le micro" }
          : { id: "allow_camera", iconName: "check-circle", label: "J'ai autorisé la caméra" }

const CHANGE_MIC: TutorHelpAction = { id: "change_mic", iconName: "microphone", label: "Changer de micro" }
const CHANGE_CAMERA: TutorHelpAction = { id: "change_camera", iconName: "video", label: "Changer de caméra" }

/**
 * Les gestes du panneau du tuteur sous ses étapes (`TroubleshootingPanel.js`), sur le web seulement :
 *  - « J'ai autorisé le micro / la caméra » (`PermissionAction`, `:199-256`) quand l'appareil est refusé
 *    (`:520-527`, `:826-833`) ; une demande relancée encore refusée le change en « Rafraîchir la page »,
 *    avec « Permission non détectée… » (`stillDenied`). En natif, l'ancien proposait « Ouvrir les
 *    paramètres », que « Toujours un problème ? » porte déjà ;
 *  - « Changer de micro » (`DeviceChangeButton kind="audioinput"`, `:641`, `:749`) : le micro autorisé, et
 *    « Je n'entends pas les élèves » ; « Changer de caméra » (`kind="videoinput"`, `:874`) : la caméra
 *    autorisée. Ils ouvrent la fenêtre des appareils, qu'il faut avoir (`canChangeDevices`).
 * Seul un refus est un refus (`useMediaPermissions`) : « on ne sait pas » garde « Changer de… ».
 */
export function tutorHelpActions(
    problem: UILiveProblem | null,
    context: TutorHelpContext,
    state: { stillDenied: { mic: boolean; camera: boolean }; canChangeDevices: boolean }
): TutorHelpAction[] {
    if (!context.web) return []
    const change = (action: TutorHelpAction) => (state.canChangeDevices ? [action] : [])
    switch (problem) {
        case "mic":
            return context.micAllowed === false ? [allowAction("mic", state.stillDenied.mic)] : change(CHANGE_MIC)
        case "cant_hear":
            return change(CHANGE_MIC)
        case "camera":
            return context.cameraAllowed === false
                ? [allowAction("camera", state.stillDenied.camera)]
                : change(CHANGE_CAMERA)
        default:
            return []
    }
}

/* ──────────────────────────── la fin ──────────────────────────── */

/**
 * Le live est déjà clôturé aujourd'hui (`LiveModeFooter.js:251`) : un journal du jour
 * `finalized_live_session` — relu à l'entrée dans l'appel, posé aussi par la réussite de la clôture.
 */
export function tutorLiveFinalized(logs: ({ action?: string | null } | null)[] | null | undefined): boolean {
    return (logs ?? []).some((log) => log?.action === "finalized_live_session")
}

/**
 * Suis-je le tuteur de la séance du jour, celui qui la clôture ? Celui que l'élève a devant lui
 * (`principalTutorId`) : le serveur le dit (`today_tutor_id`, la règle que `finalizeLiveSession` garde), la
 * copie de la séance d'un titulaire remplacé n'ayant plus la date remplacée. Une séance qui ne dit pas son
 * tuteur laisse clôturer (le serveur tranche).
 */
export function tutorClosesSession(session: LiveCastSession | null | undefined, meId: string | null): boolean {
    const closer = principalTutorId(session)
    return closer == null || closer === meId
}

/** Les reconnexions du signalement : les entrées dans l'appel du jour moins une (`LiveHelpModal.js:66-110`). */
export function tutorReportReconnections(logs: ({ action?: string | null } | null)[] | null | undefined): number {
    return Math.max(0, (logs ?? []).filter((log) => log?.action === "joined_call").length - 1)
}

/**
 * Les mots de l'ANCIENNE boîte de fin (`onExitPress`), pour que `dialog.button.clicked` continue sous
 * les mêmes valeurs (`trackDialogButton` : 15 premiers caractères, casse gardée).
 */
export const TUTOR_END_DIALOG_WORDS = {
    openTitle: "Veux-tu mettre fin au LIVE ?",
    finalize: "Clôturer et envoyer rapports",
    leaveOpen: "Quitter sans clôturer",
    finalizedTitle: "Veux-tu vraiment quitter ?",
    leave: "Quitter le LIVE",
} as const

/* ──────────────────────────── les alertes ──────────────────────────── */

/** L'alerte de la caméra masquée (`ControlBarM.web.tsx:482-500`), dite en une phrase. */
export const CAMERA_OBSTRUCTED_MESSAGE =
    "Ta caméra est peut-être masquée. Vérifie qu'aucun cache ne recouvre l'objectif."

/**
 * La barre attend le droit de publier (connexion, reprise) : l'ancienne barre cachait micro, caméra
 * et partage (`ControlBarM.web.tsx:313-324`). La barre du DS reste à l'écran ; leurs gestes ne
 * partent pas tant que la salle ne le donne pas.
 */
export function tutorCanToggleDevices(canPublish: boolean | null | undefined): boolean {
    return canPublish !== false
}
